/**
 * 005-packing-verification, admin-packing-oversight bucket. T054 — `GET /oversight/variance`,
 * broken down by packer and by SKU (FR-032, §12.6): the screen that makes a packer who never
 * records a variance visible. Same from/to period-form shape as `../reports/Variance.tsx`
 * (`URL round-trip` precedent), reading the smaller `/oversight/variance` shape instead of `008`'s
 * report envelope — see `lib/services/warehouse.ts`'s own note on why the two are distinct types.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { Text, Button } from "rizzui";
import { WidgetCard, ErrorSurface, EmptyState } from "@steelyard/theme";
import { DateField } from "../../components/date-field";
import { PagedTable } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { TableSearch } from "../../components/table/toolbar";
import { can } from "../../lib/permissions";
import { formatDate, parseIsoDate, toIsoDate } from "../../lib/format";
import {
  getOversightVariance,
  formatPercent,
  personName,
  type OversightVarianceReport,
  type OversightPackerVariance,
  type OversightSkuVariance,
} from "../../lib/services/warehouse";
import type { AccountSummary, PermissionState } from "../../types";


/**
 * A right-aligned figure column: sorts on the API's own value read as a number (the wire sends
 * decimals as `number | string`), shows it as sent. Nothing summed or derived.
 */
function figure<T>(key: keyof T & string, header: string, show: (value: T[keyof T & string]) => ReactNode = (value) => String(value)): ColumnDef<T> {
  return {
    id: key,
    accessorFn: (row) => Number(row[key]),
    header,
    meta: { align: "right" },
    cell: ({ row }) => <span className="tabular-nums">{show(row.original[key])}</span>,
  };
}

/** The packer by the API's own `packerName`; one it could not resolve reads "Unknown user", never the id. */
const packerColumns: ColumnDef<OversightPackerVariance>[] = [
  {
    id: "packerId",
    accessorFn: (row) => personName(row.packerName),
    header: "Packer",
    cell: ({ row }) => personName(row.original.packerName),
  },
  figure<OversightPackerVariance>("linesPacked", "Lines packed"),
  figure<OversightPackerVariance>("amberCount", "Amber"),
  figure<OversightPackerVariance>("redCount", "Red"),
  figure<OversightPackerVariance>("varianceRate", "Variance rate", (value) => formatPercent(value)),
  figure<OversightPackerVariance>("collisionConfirmations", "Collision confirmations"),
  figure<OversightPackerVariance>("excludedLines", "Excluded lines"),
];

const skuColumns: ColumnDef<OversightSkuVariance>[] = [
  // The SKU by name only — its id is not something a reader can use.
  { id: "skuName", accessorKey: "skuName", header: "SKU", cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.skuName}</span> },
  figure<OversightSkuVariance>("linesPacked", "Lines packed"),
  figure<OversightSkuVariance>("amberCount", "Amber"),
  figure<OversightSkuVariance>("redCount", "Red"),
  figure<OversightSkuVariance>("varianceRate", "Variance rate", (value) => formatPercent(value)),
];

/** Both breakdowns arrive whole: `PagedTable`'s whole-set mode, paged in memory. */
const whole = <T,>(items: T[]) => ({ items, page: 1, pageSize: items.length, totalCount: items.length });

export default function PackingVariancePage() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [report, setReport] = useState<OversightVarianceReport | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  const from = searchParams.get("from") ?? "";
  const to = searchParams.get("to") ?? "";
  const [fromDate, setFromDate] = useState(from);
  const [toDate, setToDate] = useState(to);

  useEffect(() => {
    run(from || undefined, to || undefined);
  }, [from, to]);

  if (!can(permissionState, "packing.oversight")) {
    return (
      <ErrorSurface
        error={{ title: "Not permitted", status: 403, detail: "The packing variance review is gated by packing.oversight, which your role does not hold." }}
        action="view the variance review"
      />
    );
  }

  async function run(nextFrom?: string, nextTo?: string) {
    setLoading(true);
    setError(null);
    try {
      setReport(await getOversightVariance({ from: nextFrom, to: nextTo }));
    } catch (err) {
      setError(err);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSearchParams(fromDate || toDate ? { from: fromDate, to: toDate } : {});
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Packing Variance"
        description="Amber and red packing events, broken down by who packed each line and by SKU. Someone who never records a variance is statistically implausible and this is the screen that makes them visible."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Packing" }, { name: "Variance" }]}
      />

      <WidgetCard title="Period" description="Optional — defaults to the last 30 days." rounded="lg" headerClassName="border-muted border-b pb-4">
        <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-4 pt-4">
          <DateField
            dateFormat="dd-MMM-yyyy"
            className="w-44"
            placeholderText="From…"
            selected={parseIsoDate(fromDate || null)}
            maxDate={parseIsoDate(toDate || null) ?? undefined}
            onChange={(date) => setFromDate(date ? toIsoDate(date as Date) : "")}
            inputProps={{ label: "From", clearable: Boolean(fromDate), onClear: () => setFromDate("") }}
          />
          <DateField
            dateFormat="dd-MMM-yyyy"
            className="w-44"
            placeholderText="To…"
            selected={parseIsoDate(toDate || null)}
            minDate={parseIsoDate(fromDate || null) ?? undefined}
            onChange={(date) => setToDate(date ? toIsoDate(date as Date) : "")}
            inputProps={{ label: "To", clearable: Boolean(toDate), onClear: () => setToDate("") }}
          />
          <Button type="submit" disabled={loading}>{loading ? "Running…" : "Run"}</Button>
        </form>
      </WidgetCard>

      {error ? <ErrorSurface error={error} action="load the packing variance review" /> : null}

      {report ? (
        <>
          <Text className="text-sm text-gray-500">
            {formatDate(report.from)} to {formatDate(report.to)}
          </Text>

          <PagedTable<OversightPackerVariance>
            key={`${report.from}-${report.to}`}
            result={whole(report.byPacker)}
            caption="Variance by who packed each line"
            columns={packerColumns}
            title="By packing operative"
            search={(table) => <TableSearch table={table} placeholder="Search packers…" />}
            empty={<EmptyState title="No packing in this period">Nothing was packed by anyone in the chosen range.</EmptyState>}
          />

          <PagedTable<OversightSkuVariance>
            key={`${report.from}-${report.to}`}
            result={whole(report.bySku)}
            caption="Variance by SKU"
            columns={skuColumns}
            title="By SKU"
            search={(table) => <TableSearch table={table} placeholder="Search SKUs…" />}
            empty={<EmptyState title="No packing in this period">No SKU was packed in the chosen range.</EmptyState>}
          />
        </>
      ) : null}
    </div>
  );
}
